/**
 * Space notification levels: per-alias defaults, per-Space / category /
 * channel overrides, and the inheritance resolver shared by client and API.
 *
 * Overrides are personal (per alias) and never materialized onto children:
 * an absent override means "inherit", so a change to a parent takes effect
 * everywhere below it immediately.
 *
 * Resolution order (first hit wins):
 * channel override → nearest category override → … → root category override →
 * Space override → alias Spaces default.
 *
 * @module api/space-notifications
 */
/**
 * - `all`: every message.
 * - `mentions`: explicit @alias, a held role (incl. Everyone), or @here.
 * - `direct`: explicit @alias or a reply to the viewer.
 * - `nothing`: no alerts and no badges.
 */
export const SPACE_NOTIFY_LEVELS = ['all', 'mentions', 'direct', 'nothing'];
/** Max category / channel override entries kept per Space. */
export const SPACE_NOTIFICATION_OVERRIDES_MAX_ENTRIES = 500;
/** Matches behaviour before notification levels existed. */
export const DEFAULT_SPACE_NOTIFICATION_DEFAULTS = Object.freeze({
    level: 'all',
    announcements: true,
    spaceChanges: true,
});
/** Space-scoped on/off alerts that sit beside the level. */
export const SPACE_NOTIFY_TOGGLES = ['announcements', 'spaceChanges'];
/**
 * What a Space edit touched, for "Space changes" alerts. Names only, never
 * values: encrypted Spaces keep their names and media opaque to the server.
 */
export const SPACE_CHANGE_KINDS = [
    'name',
    'description',
    'logo',
    'banner',
    'theme',
    'visibility',
    'access',
    'encryption',
];
export function isSpaceNotifyLevel(value) {
    return typeof value === 'string' && SPACE_NOTIFY_LEVELS.includes(value);
}
export function resolveSpaceNotifyLevel(params) {
    const { defaults, overrides, channelId, categoryPath } = params;
    if (overrides) {
        if (channelId) {
            const level = overrides.channels?.[channelId];
            if (isSpaceNotifyLevel(level))
                return { level, source: 'channel', sourceId: channelId };
        }
        for (const categoryId of categoryPath ?? []) {
            const level = overrides.categories?.[categoryId];
            if (isSpaceNotifyLevel(level))
                return { level, source: 'category', sourceId: categoryId };
        }
        if (isSpaceNotifyLevel(overrides.level))
            return { level: overrides.level, source: 'space' };
    }
    return { level: defaults.level, source: 'default' };
}
/** A Space override wins over the alias default for an on/off alert. */
export function resolveSpaceToggle(toggle, defaults, overrides) {
    const own = overrides?.[toggle];
    return typeof own === 'boolean' ? own : defaults[toggle];
}
export function resolveSpaceAnnouncements(defaults, overrides) {
    return resolveSpaceToggle('announcements', defaults, overrides);
}
/** Whether a message should alert (toast / sound / OS notification). */
export function messagePassesLevel(level, kind) {
    switch (level) {
        case 'all':
            return true;
        case 'mentions':
            return kind.direct || kind.group;
        case 'direct':
            return kind.direct;
        case 'nothing':
            return false;
    }
}
/** Whether a mention badge should show for a message of this kind. */
export function mentionPassesLevel(level, kind) {
    return level === 'nothing' ? false : level === 'direct' ? kind.direct : kind.direct || kind.group;
}
/** Only `all` channels show unread counts; other levels show mention badges only. */
export function levelShowsUnreadCount(level) {
    return level === 'all';
}
/** Whether @role / @here mentions belong in the notification inbox. */
export function levelIncludesGroupMentions(level) {
    return level === 'all' || level === 'mentions';
}
/**
 * Ancestor categories of `categoryId`, nearest first and including itself.
 * Cycle-safe; unknown ids end the walk.
 */
export function categoryAncestorPath(categoryId, categories) {
    if (!categoryId)
        return [];
    const parentById = new Map(categories.map((c) => [c.id, c.parentCategoryId ?? null]));
    const path = [];
    let current = categoryId;
    while (current && parentById.has(current) && !path.includes(current)) {
        path.push(current);
        current = parentById.get(current) ?? null;
    }
    return path;
}
function patchMap(current, patch) {
    if (!patch)
        return current;
    const next = { ...(current ?? {}) };
    for (const [id, level] of Object.entries(patch)) {
        if (level === null)
            delete next[id];
        else
            next[id] = level;
    }
    return Object.keys(next).length > 0 ? next : undefined;
}
/** Apply a PATCH to stored overrides; empty results collapse to `undefined`. */
export function applySpaceNotificationOverridesPatch(current, patch) {
    const next = {};
    const level = patch.level === undefined ? current?.level : patch.level;
    if (level)
        next.level = level;
    for (const toggle of SPACE_NOTIFY_TOGGLES) {
        const value = patch[toggle] === undefined ? current?.[toggle] : patch[toggle];
        if (typeof value === 'boolean')
            next[toggle] = value;
    }
    const categories = patchMap(current?.categories, patch.categories);
    if (categories)
        next.categories = categories;
    const channels = patchMap(current?.channels, patch.channels);
    if (channels)
        next.channels = channels;
    return Object.keys(next).length > 0 ? next : undefined;
}
//# sourceMappingURL=space-notifications.js.map